import { type RefObject, useEffect, useRef } from 'react';
import type { ScrollView } from 'react-native';
import { getScrollToNowOffset } from '../utils/calendarHourGrid';

interface UseScrollToNowOptions {
  scrollRef: RefObject<ScrollView | null>;
  /** From getNowLineRatio: null on any day that is not today. */
  nowRatio: number | null;
  gridHeight: number;
  /** From the ScrollView's onLayout; 0 until it has been measured. */
  viewportHeight: number;
}

/**
 * Puts "now" on screen once, when the grid first opens.
 *
 * The day and week grids start at the top of their window every time, so a rep
 * opening the calendar at 3pm lands on the morning and scrolls. This scrolls
 * for them — and then never again: the grid re-renders on every realtime
 * invalidation, and re-centring there would yank the view out from under
 * someone mid-read. Once done, the scroll position belongs to the reader.
 */
export function useScrollToNow({
  scrollRef,
  nowRatio,
  gridHeight,
  viewportHeight,
}: UseScrollToNowOptions): void {
  const hasScrolled = useRef(false);

  useEffect(() => {
    if (hasScrolled.current) return;
    if (nowRatio === null || viewportHeight <= 0) return;

    const y = getScrollToNowOffset(nowRatio, gridHeight, viewportHeight);
    hasScrolled.current = true;
    // Not animated: this is where the grid opens, not a movement to follow.
    scrollRef.current?.scrollTo({ y, animated: false });
  }, [scrollRef, nowRatio, gridHeight, viewportHeight]);
}
